import { Component, Prop, h } from '@stencil/core';
import { getConstant } from '../../../../../utils/constants-helpers';
import { parseJsonProp } from '../../../../../utils/helpers/hydration';

/**
 * Call-to-action component for encouraging user engagement
 *
 * @product signnow
 * @version 1.0.2
 * @category SignNow
 * @contentConfig Call-to-action component for encouraging user engagement and conversions
 * @example <sn-call-to-action-type-2 constants='{"CALL_TO_ACTION_TYPE_2_HEADING":"Get legally-binding signatures now!","CALL_TO_ACTION_TYPE_2_DESCRIPTION":"be ready to get more","CALL_TO_ACTION_TYPE_2_PRIMARY_BUTTON":"Start free trial","CALL_TO_ACTION_TYPE_2_SECONDARY_BUTTON":"Request a call","LINK__CALL_TO_ACTION_TYPE_2_PRIMARY_BUTTON":"https://snseats.signnow.com/purchase/business_free_trial/loggedout","LINK__CALL_TO_ACTION_TYPE_2_SECONDARY_BUTTON":"/contact","CALL_TO_ACTION_TYPE_2_LIST_ITEM_1_TITLE":"Best ROI.","CALL_TO_ACTION_TYPE_2_LIST_ITEM_1_DESC":"Our customers achieve an average 7x ROI within the first six months.","CALL_TO_ACTION_TYPE_2_LIST_ITEM_2_TITLE":"Scales with your use cases.","CALL_TO_ACTION_TYPE_2_LIST_ITEM_2_DESC":"From SMBs to mid-market, airSlate SignNow delivers results for businesses of all sizes.","CALL_TO_ACTION_TYPE_2_LIST_ITEM_3_TITLE":"Intuitive UI and API.","CALL_TO_ACTION_TYPE_2_LIST_ITEM_3_DESC":"Sign and send documents from your apps in minutes."}'></sn-call-to-action-type-2>
 * @constant CALL_TO_ACTION_TYPE_2_HEADING - "Get legally-binding signatures now!" - Main heading for call-to-action section
 * @constant CALL_TO_ACTION_TYPE_2_DESCRIPTION - "Sign up now and unlock your full potential with our platform." - Description text for call-to-action section
 * @constant CALL_TO_ACTION_TYPE_2_PRIMARY_BUTTON - "Start free trial" - Primary action button text
 * @constant CALL_TO_ACTION_TYPE_2_SECONDARY_BUTTON - "Request a call" - Secondary action button text
 * @constant LINK__CALL_TO_ACTION_TYPE_2_PRIMARY_BUTTON - "https://snseats.signnow.com/purchase/business_free_trial/loggedout" - Link for the "Start free trial" button
 * @constant LINK__CALL_TO_ACTION_TYPE_2_SECONDARY_BUTTON - "/contact" - Link for the "Request a call" button
 * @constant CALL_TO_ACTION_TYPE_2_LIST_ITEM_1_TITLE - "Best ROI." - First list item title
 * @constant CALL_TO_ACTION_TYPE_2_LIST_ITEM_1_DESC - "Our customers achieve an average 7x ROI within the first six months." - First list item description
 * @constant CALL_TO_ACTION_TYPE_2_LIST_ITEM_2_TITLE - "Scales with your use cases." - Second list item title
 * @constant CALL_TO_ACTION_TYPE_2_LIST_ITEM_2_DESC - "From SMBs to mid-market, airSlate SignNow delivers results for businesses of all sizes." - Second list item description
 * @constant CALL_TO_ACTION_TYPE_2_LIST_ITEM_3_TITLE - "Intuitive UI and API." - Third list item title
 * @constant CALL_TO_ACTION_TYPE_2_LIST_ITEM_3_DESC - "Sign and send documents from your apps in minutes" - Third list item description
 * @slot heading - {required}, <me-heading variant="2"> - Main CTA heading
 * @slot description - <me-text> - CTA description text
 * @slot action-primary - {required}, <me-button variant="primary"> - Primary action button
 * @slot action-secondary - <me-button variant="secondary"> - Secondary action button
 * @slot media - {required}, <me-picture> - Hero image for visual appeal
 */
@Component({
  tag: 'sn-call-to-action-type-2',
  styleUrl: 'index.scss',
  shadow: false,
})
export class SNCallToActionType2 {
  /**
   * Component constants object containing all required text constants
   */
  @Prop({ mutable: true }) constants?: Record<string, string> | string;

  /**
   * HTML tag for the heading element (h1, h2, h3, h4, h5, h6)
   * @default "h2"
   */
  @Prop() headingTag: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' = 'h2';

  componentWillLoad() {
    this.constants = parseJsonProp(this.constants);
  }

  render() {
    return (
      <call-to-action-type-2 class="sn-call-to-action-type-2">
        <me-text slot="heading" class="me-text--label">
          {getConstant(this.constants, 'CALL_TO_ACTION_TYPE_2_DESCRIPTION', 'be ready to get more')}
        </me-text>
        <me-heading slot="description" variant="2" tag={this.headingTag}>
          {getConstant(this.constants, 'CALL_TO_ACTION_TYPE_2_HEADING', 'Get legally-binding signatures now!')}
        </me-heading>
        <me-button
          slot="action-primary"
          variant="primary"
          href={getConstant(this.constants, 'LINK__CALL_TO_ACTION_TYPE_2_PRIMARY_BUTTON', 'https://snseats.signnow.com/purchase/business_free_trial/loggedout')}
        >
          {getConstant(this.constants, 'CALL_TO_ACTION_TYPE_2_PRIMARY_BUTTON', 'Start free trial')}
        </me-button>
        <me-button slot="action-secondary" variant="secondary" appearance="dark" href={getConstant(this.constants, 'LINK__CALL_TO_ACTION_TYPE_2_SECONDARY_BUTTON', '/contact')}>
          {getConstant(this.constants, 'CALL_TO_ACTION_TYPE_2_SECONDARY_BUTTON', 'Request a call')}
        </me-button>
        <div slot="media" class="page-section__column page-section__column--list">
          <ul class="cta-list">
            <li class="cta-list__item">
              <div class="cta-list__logo">
                <me-icon name="https://marketing-static.signnow.com/347/images/me/cta-type-2/icon-1.svg" />
              </div>
              <div class="cta-list__description">
                <strong>{getConstant(this.constants, 'CALL_TO_ACTION_TYPE_2_LIST_ITEM_1_TITLE', 'Best ROI.')}</strong>{' '}
                {getConstant(this.constants, 'CALL_TO_ACTION_TYPE_2_LIST_ITEM_1_DESC', 'Our customers achieve an average 7x ROI within the first six months.')}
              </div>
            </li>
            <li class="cta-list__item">
              <div class="cta-list__logo">
                <me-icon name="https://marketing-static.signnow.com/347/images/me/cta-type-2/icon-2.svg" />
              </div>
              <div class="cta-list__description">
                <strong>{getConstant(this.constants, 'CALL_TO_ACTION_TYPE_2_LIST_ITEM_2_TITLE', 'Scales with your use cases.')}</strong>{' '}
                {getConstant(this.constants, 'CALL_TO_ACTION_TYPE_2_LIST_ITEM_2_DESC', 'From SMBs to mid-market, airSlate SignNow delivers results for businesses of all sizes.')}
              </div>
            </li>
            <li class="cta-list__item">
              <div class="cta-list__logo">
                <me-icon name="https://marketing-static.signnow.com/347/images/me/cta-type-2/icon-3.svg" />
              </div>
              <div class="cta-list__description">
                <strong>{getConstant(this.constants, 'CALL_TO_ACTION_TYPE_2_LIST_ITEM_3_TITLE', 'Intuitive UI and API.')}</strong>{' '}
                {getConstant(this.constants, 'CALL_TO_ACTION_TYPE_2_LIST_ITEM_3_DESC', 'Sign and send documents from your apps in minutes.')}
              </div>
            </li>
          </ul>
        </div>
      </call-to-action-type-2>
    );
  }
}
